"use client";

/**
 * "Can't find your residence?" - shown in the /residences catalog's
 * empty search state. Anonymous visitors can submit too (identity =
 * the anon client id), and each identity may hold ONE open request at
 * a time; the admin triages them on the Residence inbox page.
 */
import { useEffect, useState } from "react";
import { Loader2, MailQuestion, CheckCircle2 } from "lucide-react";
import { API_BASE } from "@/lib/api";
import { getClientId } from "@/lib/client-id";
import type { Locale } from "@/lib/i18n-config";
import { t as translate, type TranslationKey } from "@/lib/i18n";

const NAME_MAX = 160;
const NOTE_MAX = 300;

export default function ResidenceRequestForm({
  defaultName,
  locale,
}: {
  defaultName: string;
  locale: Locale;
}) {
  const t = (k: TranslationKey) => translate(k, locale);

  const [name, setName] = useState(defaultName.slice(0, NAME_MAX));
  const [note, setNote] = useState("");
  const [busy, setBusy] = useState(false);
  // idle | done | already | error
  const [state, setState] = useState<"idle" | "done" | "already" | "error">("idle");

  // Prefill follows the live search box while the user hasn't typed
  // into the name field themselves.
  const [touched, setTouched] = useState(false);
  useEffect(() => {
    if (!touched) setName(defaultName.slice(0, NAME_MAX));
  }, [defaultName, touched]);

  // An identity with an open request goes straight to the
  // "already requested" state instead of offering the form again.
  useEffect(() => {
    let cancelled = false;
    void fetch(`${API_BASE}/residences/requests/me`, {
      credentials: "include",
      headers: { "X-Client-Id": getClientId() },
    })
      .then((r) => (r.ok ? r.json() : null))
      .then((d) => {
        if (!cancelled && d?.open) setState("already");
      })
      .catch(() => {});
    return () => { cancelled = true; };
  }, []);

  async function submit() {
    if (busy || name.trim().length < 2) return;
    setBusy(true);
    try {
      const res = await fetch(`${API_BASE}/residences/requests`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          "X-Client-Id": getClientId(),
          "X-Locale": locale,
        },
        credentials: "include",
        body: JSON.stringify({
          name: name.trim(),
          note: note.trim() || null,
        }),
      });
      if (res.status === 409) {
        setState("already");
      } else if (res.ok) {
        setState("done");
      } else {
        setState("error");
      }
    } catch {
      setState("error");
    } finally {
      setBusy(false);
    }
  }

  if (state === "done" || state === "already") {
    return (
      <div className="mt-4 mx-auto max-w-md rounded-2xl border border-emerald-200 bg-emerald-50 p-4 text-center">
        <CheckCircle2 className="w-5 h-5 text-emerald-600 mx-auto mb-1.5" />
        <p className="text-sm font-medium text-emerald-800">
          {state === "done"
            ? t("residences.request.success")
            : t("residences.request.already_open")}
        </p>
      </div>
    );
  }

  return (
    <div className="mt-4 mx-auto max-w-md rounded-2xl border border-gray-200 bg-white p-4 text-left">
      <p className="flex items-center gap-2 text-sm font-semibold text-gray-900 mb-2">
        <MailQuestion className="w-4 h-4 text-gray-400" />
        {t("residences.request.title")}
      </p>
      <input
        value={name}
        onChange={(e) => { setTouched(true); setName(e.target.value.slice(0, NAME_MAX)); }}
        placeholder={t("residences.request.name_label")}
        className="w-full rounded-xl border border-gray-300 px-3 py-2 text-sm mb-2 focus:outline-none focus:ring-2 focus:ring-indigo-500/40"
      />
      <input
        value={note}
        onChange={(e) => setNote(e.target.value.slice(0, NOTE_MAX))}
        placeholder={t("residences.request.note_label")}
        className="w-full rounded-xl border border-gray-300 px-3 py-2 text-sm mb-3 focus:outline-none focus:ring-2 focus:ring-indigo-500/40"
      />
      <button
        type="button"
        onClick={() => void submit()}
        disabled={busy || name.trim().length < 2}
        className="inline-flex items-center gap-1.5 rounded-xl bg-indigo-600 px-4 py-2 text-xs font-semibold text-white hover:bg-indigo-700 disabled:opacity-50"
      >
        {busy && <Loader2 className="w-3.5 h-3.5 animate-spin" />}
        {t("residences.request.submit")}
      </button>
      {state === "error" && (
        <p className="mt-2 text-xs font-medium text-rose-600">
          {t("residence.comments.error")}
        </p>
      )}
    </div>
  );
}
